import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { RevenueDataPoint } from '../../../types/dashboard';
import { useLocalization } from '../../../contexts/LocalizationContext';

interface RevenueTrendChartProps {
  data: RevenueDataPoint[];
  height?: number;
  showArea?: boolean;
  className?: string;
}

const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({ 
  data, 
  height = 300, 
  showArea = true,
  className = ''
}) => {
  const { strings, language } = useLocalization();
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-GB', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      month: 'short',
      day: 'numeric'
    });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">
            {formatDate(label)}
          </p>
          <div className="space-y-1">
            {payload[0] && payload[0].value !== undefined && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-accent rounded-full"></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">{strings.dashboard.revenue}:</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-white">
                  {formatCurrency(payload[0].value)}
                </span>
              </div>
            )}
            {payload[1] && payload[1].value !== undefined && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red rounded-full"></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">{strings.dashboard.expenses}:</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-white">
                  {formatCurrency(payload[1].value)}
                </span>
              </div>
            )}
            {payload[2] && payload[2].value !== undefined && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green rounded-full"></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">{strings.dashboard.profit}:</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-white">
                  {formatCurrency(payload[2].value)}
                </span>
              </div>
            )}
            {payload[3] && payload[3].value !== undefined && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="text-sm text-slate-600 dark:text-slate-300">Cases:</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-white">
                  {payload[3].value}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (showArea) {
    return (
      <div className={`w-full ${className}`}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="#64748B"
              fontSize={12}
            />
            <YAxis 
              tickFormatter={(value) => formatCurrency(value)}
              stroke="#64748B"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#C9A14B"
              fill="#C9A14B"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="expenses"
              stroke="#D32F2F"
              fill="#D32F2F"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="profit"
              stroke="#2E7D32"
              fill="#2E7D32"
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            stroke="#64748B"
            fontSize={12}
          />
          <YAxis 
            tickFormatter={(value) => formatCurrency(value)}
            stroke="#64748B"
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="revenue"
            stroke="#3B82F6"
            strokeWidth={3}
            dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
          />
          <Line
            type="monotone"
            dataKey="expenses"
            stroke="#EF4444"
            strokeWidth={3}
            dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#EF4444', strokeWidth: 2 }}
          />
          <Line
            type="monotone"
            dataKey="profit"
            stroke="#10B981"
            strokeWidth={3}
            dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueTrendChart;
